import RTSPTargetFields from "./RTSPTargetFields";
import { normalizeRTSP, validateRTSP } from "../rtspTarget.mjs";
import { defaultSEI, validateSEI } from "../sei.mjs";
import WhipParams from "./WhipParams";
import { normalizeWhip, validateWhip } from "../whip.mjs";
import LlHlsParams from "./LlHlsParams";
import { normalizeLlHls, validateLlHls } from "../llHls.mjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Formik, useFormikContext } from "formik";
import DefaultPropsProvider from "@mui/material/DefaultPropsProvider";
import * as Yup from "yup";
import type { PcapReplaySource, StreamTargetForm } from "../types/streamTargets";
import SrtInputParams, { createDefaultSrtInputParams } from "./SrtInputParams";
import TuneRounded from "@mui/icons-material/TuneRounded";
import OutputRounded from "@mui/icons-material/OutputRounded";
import { alpha } from "@mui/material/styles";

type StreamTargetDlgProps = {
  readOnly?: boolean;
  open: boolean;
  initialTarget?: StreamTargetForm | null;
  onClose: () => void;
  onSave: (target: StreamTargetForm) => void;
  networkInterfaceOptions: string[];
  isNetworkInterfacesLoading: boolean;
  loadNetworkInterfaces: (currentValue?: string) => Promise<void>;
  reservedNames: string[];
  platformName?: string;
  streamName?: string;
  capturedDestination?: { ip: string; port: number } | null;
  rawPcapEnabled?: boolean;
  pcapSource?: PcapReplaySource | null;
  authorizedFetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  flywheelEnabled?: boolean;
  flywheelProcessorId?: string;
  showInputSelector?: boolean;
  udsDatagramAvailable?: boolean;
};

const TARGET_TYPE_OPTIONS = [
  { value: "udp", label: "UDP", disabled: false },
  { value: "uds", label: "UDS", disabled: false },
  { value: "srt", label: "SRT", disabled: false },
  { value: "whip", label: "WebRTC / WHIP", disabled: false },
  { value: "llhls", label: "LL-HLS", disabled: false },
  { value: "rawPcap", label: "Raw PCAP", disabled: false },
  { value: "rtsp", label: "RTSP Server", disabled: false }
] as const;

const TYPE_URL_PREFIX: Record<StreamTargetForm["type"], string> = {
  udp: "udp",
  srt: "srt",
  uds: "uds",
  rtsp: "rtsp",
  llhls: "",
  whip: "",
  rawPcap: ""
};

const getTypeProtocolPrefix = (type: StreamTargetForm["type"]): string => {
  const normalizedPrefix = TYPE_URL_PREFIX[type].trim().toLowerCase();
  if (!normalizedPrefix) {
    return "";
  }
  return normalizedPrefix.endsWith("://") ? normalizedPrefix : `${normalizedPrefix}://`;
};

const stripTypeProtocolFromUrl = (url: string, protocol: string): string => {
  const lowerProtocol = protocol.toLowerCase();
  const lowerUrl = url.toLowerCase();
  const remainder = lowerUrl.startsWith(lowerProtocol) ? url.slice(protocol.length) : url;
  return remainder.replace(/^\s+/, "");
};

const isValidIpv4 = (value: string): boolean => {
  if (!value) {
    return false;
  }
  const octets = value.split(".");
  if (octets.length !== 4) {
    return false;
  }
  return octets.every((octet) => {
    if (!/^\d{1,3}$/.test(octet)) {
      return false;
    }
    const numeric = Number(octet);
    return numeric >= 0 && numeric <= 255;
  });
};

const deriveRawDestinationMac = (value: string): string => {
  if (!isValidIpv4(value)) {
    return "";
  }
  const octets = value.split(".").map(Number);
  if (octets.every((octet) => octet === 255)) {
    return "ff:ff:ff:ff:ff:ff";
  }
  if (octets[0] >= 224 && octets[0] <= 239) {
    return [0x01, 0x00, 0x5e, octets[1] & 0x7f, octets[2], octets[3]]
      .map((octet) => octet.toString(16).padStart(2, "0"))
      .join(":");
  }
  return "";
};

const isValidIpLiteral = (value: string): boolean => {
  const normalized = value.replace(/^\[|\]$/g, "");
  if (isValidIpv4(normalized)) {
    return true;
  }
  return normalized.includes(":") && /^[0-9a-f:.]+$/i.test(normalized);
};

const WINDOWS_NAMED_PIPE_PREFIX = /^\\\\\.\\pipe\\/i;
const WINDOWS_DRIVE_PATH_PREFIX = /^[a-z]:[\\/]/i;
const UDS_NAME_PATTERN = /^[a-z0-9._-]+(?:[\\/][a-z0-9._-]+)*$/i;

const isValidUdsSocketPathValue = (value: string): boolean => {
  let trimmed = value.trim();
  if (!trimmed || trimmed.includes("\u0000")) {
    return false;
  }
  const udsPrefix = "uds://";
  if (trimmed.toLowerCase().startsWith(udsPrefix)) {
    trimmed = trimmed.slice(udsPrefix.length).trim();
  }
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith("/")) {
    return trimmed.length <= 107;
  }
  if (WINDOWS_NAMED_PIPE_PREFIX.test(trimmed)) {
    return true;
  }
  if (WINDOWS_DRIVE_PATH_PREFIX.test(trimmed)) {
    return true;
  }
  return UDS_NAME_PATTERN.test(trimmed);
};

const isMulticastUrl = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  try {
    const url = new URL(trimmed);
    const hostname = url.hostname;
    if (!hostname) {
      return false;
    }

    if (!/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return false;
    }

    const octets = hostname.split(".").map((part) => Number.parseInt(part, 10));
    if (octets.length !== 4 || octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
      return false;
    }

    return octets[0] >= 224 && octets[0] <= 239;
  } catch {
    return false;
  }
};

const sanitizeUdsSegment = (value: string, fallback: string): string => {
  const trimmed = value.trim();
  const cleaned = trimmed.replace(/[\s;&|$><`"'*?!(){}\[\]\/\\]+/g, "");
  return cleaned || fallback;
};

const buildDefaultUdsUrl = (platformName: string, streamName: string, outputName: string): string => {
  const platformSegment = sanitizeUdsSegment(platformName, "");
  const streamSegment = sanitizeUdsSegment(streamName, "stream");
  const outputSegment = sanitizeUdsSegment(outputName, "output");
  const fileSegment = platformSegment
    ? `${platformSegment}.${streamSegment}.${outputSegment}.sock`
    : `${streamSegment}.${outputSegment}.sock`;
  return `${getTypeProtocolPrefix("uds")}/tmp/${fileSegment}`;
};

const createEmptyTarget = (): StreamTargetForm => ({
  rtsp: normalizeRTSP(),
  llhls: normalizeLlHls(),
        whip: normalizeWhip(),
  payloadMode: "fullStream",
  sei: defaultSEI(),
  id: "",
  inputType: "source",
  inputId: "",
  name: "",
  description: "",
  type: "udp",
  destinationMode: "configured",
  url: "",
  networkInterface: "",
  ttl: 64,
  bufferSize: DEFAULT_TARGET_BUFFER_SIZE,
  udsMode: "connection",
  srt: createDefaultSrtInputParams(),
  rawPcap: {
    mode: "rewrite",
    interfaceId: "",
    destinationIp: "",
    destinationPort: "",
    destinationMac: "",
    allowSpoofedSource: false,
    queueSize: 256
  }
});

const DEFAULT_TARGET_BUFFER_SIZE = 4096;
const MINIMUM_SRT_OUTPUT_BUFFER_SIZE = 4 * 1024 * 1024;

type RawPcapInterface = {
  id: string;
  displayName: string;
  addresses: string[] | null;
  mtu: number;
  flags: string;
  linkType?: string;
  injectionAvailable: boolean;
  error?: string;
};

const RawPcapFields = ({
  enabled,
  source,
  authorizedFetch,
  setPreflightReady,
  readOnly = false
}: {
  readOnly?: boolean;
  enabled: boolean;
  source: PcapReplaySource | null;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  setPreflightReady: (ready: boolean) => void;
}) => {
  const { values, setFieldValue } = useFormikContext<StreamTargetForm>();
  const [interfaces, setInterfaces] = useState<RawPcapInterface[]>([]);
  const [loadingInterfaces, setLoadingInterfaces] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [resolvedDestinationMac, setResolvedDestinationMac] = useState("");
  const config = values.rawPcap;
  const sourceFile = source?.file ?? "";
  const sourceHostMediaDrive = source?.hostMediaDrive ?? "";
  const sourceIp = source?.sourceIp ?? "";
  const sourceDestinationIp = source?.destinationIp ?? "";
  const sourcePort = source?.sourcePort ?? 0;
  const sourceDestinationPort = source?.destinationPort ?? 0;

  useEffect(() => {
    if (!enabled || readOnly) {
      return;
    }
    let cancelled = false;
    setLoadingInterfaces(true);
    void authorizedFetch("/api/network/pcap-interfaces")
      .then(async (response) => {
        const payload = await response.json().catch(() => null) as
          | { interfaces?: RawPcapInterface[]; message?: string; error?: string }
          | null;
        if (!response.ok) {
          throw new Error(payload?.message ?? payload?.error ?? "Unable to list PCAP interfaces");
        }
        if (!cancelled) {
          setInterfaces(
            (Array.isArray(payload?.interfaces) ? payload.interfaces : []).map((item) => ({
              ...item,
              addresses: Array.isArray(item.addresses) ? item.addresses : []
            }))
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setPreflightError(error instanceof Error ? error.message : "Unable to list PCAP interfaces");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingInterfaces(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, enabled, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    if (values.type !== "rawPcap") {
      setPreflightReady(true);
      return;
    }
    if (!enabled) {
      setPreflightReady(false);
      setPreflightError("Raw PCAP replay is unavailable in this build or stream mode.");
      return;
    }
    setPreflightReady(false);
    setPreflightLoading(false);
    setResolvedDestinationMac("");
    setWarnings([]);
    if (!sourceFile) {
      setPreflightError("Select a PCAP source flow first.");
      return;
    }
    if (!config.interfaceId) {
      setPreflightError("Select a PCAP output interface.");
      return;
    }
    if (config.mode === "rewrite" && !isValidIpv4(config.destinationIp)) {
      setPreflightError("Enter a valid destination IPv4 address.");
      return;
    }
    if (
      config.mode === "rewrite" &&
      (typeof config.destinationPort !== "number" ||
        config.destinationPort < 1 ||
        config.destinationPort > 65535)
    ) {
      setPreflightError("Enter a UDP destination port from 1 to 65535.");
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const preflightConfig = {
      mode: config.mode,
      interfaceId: config.interfaceId,
      destinationIp: config.destinationIp,
      destinationPort: config.destinationPort,
      destinationMac: config.destinationMac,
      allowSpoofedSource: config.allowSpoofedSource,
      queueSize: config.queueSize
    };
    const timer = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      setPreflightLoading(true);
      setPreflightError("");
      void authorizedFetch("/api/network/raw-pcap/preflight", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          file: sourceFile,
          hostMediaDrive: sourceHostMediaDrive,
          sourceIp,
          sourceDestinationIp,
          sourcePort,
          sourceDestinationPort,
          config: preflightConfig
        })
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => null) as
            | { destinationMac?: string; warnings?: string[]; code?: string; message?: string }
            | null;
          if (!response.ok) {
            throw new Error(
              [payload?.code, payload?.message].filter(Boolean).join(": ") ||
                "Raw PCAP preflight failed"
            );
          }
          if (cancelled) {
            return;
          }
          setResolvedDestinationMac(payload?.destinationMac ?? "");
          setWarnings(Array.isArray(payload?.warnings) ? payload.warnings : []);
          setPreflightReady(true);
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          setPreflightError(error instanceof Error ? error.message : "Raw PCAP preflight failed");
          setPreflightReady(false);
        })
        .finally(() => {
          if (!cancelled) {
            setPreflightLoading(false);
          }
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    authorizedFetch,
    readOnly,
    config.allowSpoofedSource,
    config.destinationIp,
    config.destinationMac,
    config.destinationPort,
    config.interfaceId,
    config.mode,
    config.queueSize,
    enabled,
    setPreflightReady,
    sourceDestinationIp,
    sourceDestinationPort,
    sourceFile,
    sourceHostMediaDrive,
    sourceIp,
    sourcePort,
    values.type
  ]);

  const selectedInterface = interfaces.find((item) => item.id === config.interfaceId);
  const automaticDestinationMac = config.mode === "rewrite" ? deriveRawDestinationMac(config.destinationIp) : "";
  const displayedDestinationMac = automaticDestinationMac || resolvedDestinationMac || config.destinationMac;
  const destinationMacIsDerived = Boolean(automaticDestinationMac || (resolvedDestinationMac && resolvedDestinationMac !== config.destinationMac));
  return (
    <Stack spacing={1.5}>
      <Alert severity="warning">
        Raw replay injects Ethernet frames directly. Host preflight cannot verify switch ACLs,
        port security, multicast routing, or receiver reachability.
      </Alert>
      <TextField
        name="rawPcap.mode"
        label="Replay mode"
        select
        value={config.mode}
        onChange={(event) => void setFieldValue("rawPcap.mode", event.target.value)}
        fullWidth
      >
        <MenuItem value="original">Original frame (unchanged)</MenuItem>
        <MenuItem value="rewrite">Rewrite destination</MenuItem>
      </TextField>
      {config.mode === "original" ? (
        <Alert severity="warning">
          The captured source and destination MAC/IP/UDP fields will be transmitted unchanged.
        </Alert>
      ) : null}
      <TextField
        name="rawPcap.interfaceId"
        label="PCAP output interface"
        select
        value={config.interfaceId}
        onChange={(event) => void setFieldValue("rawPcap.interfaceId", event.target.value)}
        fullWidth
        disabled={readOnly || loadingInterfaces}
        helperText={selectedInterface?.error || (selectedInterface ? `MTU ${selectedInterface.mtu} - ${selectedInterface.linkType ?? "unknown link type"}` : "Select the physical output interface")}
      >
        {readOnly && !interfaces.some((item) => item.id === config.interfaceId) ? (
          <MenuItem value={config.interfaceId}>{config.interfaceId}</MenuItem>
        ) : null}
        {interfaces.map((item) => (
          <MenuItem key={item.id} value={item.id} disabled={!item.injectionAvailable}>
            {item.displayName} {Array.isArray(item.addresses) && item.addresses.length ? `(${item.addresses.join(", ")})` : ""}
          </MenuItem>
        ))}
      </TextField>
      {config.mode === "rewrite" ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              name="rawPcap.destinationIp"
              label="Destination IPv4"
              value={config.destinationIp}
              onChange={(event) => void setFieldValue("rawPcap.destinationIp", event.target.value)}
              fullWidth
            />
            <TextField
              name="rawPcap.destinationPort"
              label="UDP port"
              type="number"
              value={config.destinationPort}
              onChange={(event) => void setFieldValue("rawPcap.destinationPort", event.target.value === "" ? "" : Number(event.target.value))}
              slotProps={{ htmlInput: { min: 1, max: 65535 } }}
              fullWidth
            />
          </Stack>
          <TextField
            name="rawPcap.destinationMac"
            label="Destination MAC / next hop"
            value={displayedDestinationMac}
            onChange={(event) => void setFieldValue("rawPcap.destinationMac", event.target.value)}
            helperText="Derived automatically for multicast/broadcast; required manually for unicast."
            disabled={readOnly || destinationMacIsDerived}
            fullWidth
          />
        </>
      ) : null}
      <TextField
        name="rawPcap.queueSize"
        label="Queue size"
        type="number"
        value={config.queueSize}
        onChange={(event) => void setFieldValue("rawPcap.queueSize", event.target.value === "" ? "" : Number(event.target.value))}
        slotProps={{ htmlInput: { min: 1, max: 65536 } }}
        fullWidth
      />
      <FormControlLabel
        control={
          <Switch
            checked={config.allowSpoofedSource}
            onChange={(event) => void setFieldValue("rawPcap.allowSpoofedSource", event.target.checked)}
          />
        }
        label="Allow captured source IP not assigned to this interface"
      />
      {config.allowSpoofedSource ? (
        <Alert severity="warning">Source spoofing may violate network policy and trigger port-security controls.</Alert>
      ) : null}
      {preflightLoading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Validating a sample of the selected UDP flow...</Typography>
        </Stack>
      ) : null}
      {preflightError ? <Alert severity="error">{preflightError}</Alert> : null}
      {warnings.map((warning) => <Alert key={warning} severity="warning">{warning}</Alert>)}
    </Stack>
  );
};


const StreamTargetDlg = ({
  readOnly = false,
  open,
  initialTarget,
  onClose,
  onSave,
  networkInterfaceOptions,
  isNetworkInterfacesLoading,
  loadNetworkInterfaces,
  reservedNames,
  platformName = "",
  streamName = "",
  capturedDestination,
  rawPcapEnabled = false,
  pcapSource = null,
  authorizedFetch = fetch,
  flywheelEnabled = false,
  flywheelProcessorId = "",
  showInputSelector = true,
  udsDatagramAvailable = true,
}: StreamTargetDlgProps) => {
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const isEditing = Boolean(initialTarget?.id);
  const [showAdvanced, setShowAdvanced] = useState(Boolean(initialTarget));
	const [rawPreflightReady, setRawPreflightReady] = useState(false);
  const lastAutoUrlRef = useRef<string>("");
  const defaultUdsUrl = useMemo(
    () => buildDefaultUdsUrl(platformName, streamName, ""),
    [platformName, streamName]
  );
  const normalizedReservedNames = useMemo(
    () =>
      reservedNames
        .map((name) => name?.trim().toLowerCase())
        .filter((name) => Boolean(name)),
    [reservedNames]
  );

  useEffect(() => {
    if (!open || initialTarget) {
      lastAutoUrlRef.current = "";
      return;
    }
    lastAutoUrlRef.current = defaultUdsUrl;
  }, [defaultUdsUrl, initialTarget, open]);

  const initialValues = useMemo(
    () => ({
      ...createEmptyTarget(),
      payloadMode: initialTarget?.payloadMode ?? "fullStream",
      sei: initialTarget?.sei ?? defaultSEI(),
      id: initialTarget?.id ?? "",
      inputType: initialTarget?.inputType ?? "source",
      inputId: initialTarget?.inputId ?? "",
      originalTarget: initialTarget?.originalTarget ? { ...initialTarget.originalTarget } : undefined,
      originalEngine: initialTarget?.originalEngine ? { ...initialTarget.originalEngine } : undefined,
      additionalEngines: initialTarget?.additionalEngines?.map((engine) => ({ ...engine })),
      preserveOriginal: initialTarget?.preserveOriginal ?? false,
      name: initialTarget?.name ?? "",
      description: initialTarget?.description ?? "",
      type: initialTarget?.type ?? "udp",
      destinationMode: initialTarget?.destinationMode ?? "configured",
      url: initialTarget?.url ?? "",
      networkInterface: initialTarget?.networkInterface ?? "",
      ttl: initialTarget?.ttl ?? 64,
      bufferSize: initialTarget?.bufferSize ?? DEFAULT_TARGET_BUFFER_SIZE,
      udsMode: initialTarget?.udsMode ?? "connection",
      srt: initialTarget?.srt ?? createDefaultSrtInputParams(),
      rtsp: normalizeRTSP(initialTarget?.rtsp),
      llhls: normalizeLlHls(initialTarget?.llhls),
      whip: normalizeWhip(initialTarget?.whip),
      rawPcap: initialTarget?.rawPcap ?? createEmptyTarget().rawPcap
    }),
    [initialTarget]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .required("Name is required")
          .test("unique-name", "Name must be unique", (value) => {
            const normalized = value?.trim().toLowerCase();
            if (!normalized) {
              return true;
            }
            return !normalizedReservedNames.includes(normalized);
          }),
        rtsp: Yup.mixed().test("rtsp", "Invalid RTSP settings", function(value) { if (this.parent.type !== "rtsp") return true; const message = validateRTSP(value); return message ? this.createError({ message }) : true; }),
        whip: Yup.mixed().test("whip", "Invalid WHIP settings", function(value) {
          if (this.parent.type !== "whip") return true;
          const message = validateWhip(value, this.parent.url);
          return message ? this.createError({ message }) : true;
        }),
        llhls: Yup.mixed().test("llhls", "Invalid LL-HLS settings", function(value) {
          if (this.parent.type !== "llhls") return true;
          const message = validateLlHls(value);
          return message ? this.createError({ message }) : true;
        }),
        payloadMode: Yup.string().oneOf(["fullStream", "h264Sei"]),
        sei: Yup.object().test("sei", "Invalid SEI settings", function(value) {
          if (this.parent.type !== "udp" || this.parent.payloadMode !== "h264Sei") return true;
          const message = validateSEI(value as StreamTargetForm["sei"]);
          return message ? this.createError({ message }) : true;
        }),
        description: Yup.string().transform((value) => value?.trim() ?? ""),
        type: Yup.string()
          .transform((value) => {
            const normalized = value?.trim().toLowerCase();
            return normalized === "rawpcap" ? "rawPcap" : normalized || "udp";
          })
          .oneOf(["udp", "srt", "uds", "rtsp", "rawPcap", "llhls", "whip"], "Select a valid type")
          .required("Type is required"),
        destinationMode: Yup.string()
          .oneOf(["configured", "captured"], "Select a destination mode")
          .required("Destination mode is required"),
        ttl: Yup.number()
          .typeError("TTL must be a number")
          .min(1, "TTL must be positive")
          .max(255, "TTL must be 1-255")
          .when("type", {
            is: (value: string) => value?.toLowerCase?.() === "udp",
            then: (schema) => schema.required("TTL is required"),
            otherwise: (schema) => schema.notRequired()
          }),
        bufferSize: Yup.number()
          .typeError("Buffer size must be a number")
          .min(1, "Buffer size must be positive")
          .when("type", {
            is: (value: string) => {
              const normalized = value?.toLowerCase?.();
              return normalized === "udp" || normalized === "uds";
            },
            then: (schema) => schema.required("Buffer size is required"),
            otherwise: (schema) => schema.notRequired()
          }),
        udsMode: Yup.string()
          .oneOf(["connection", "datagram"], "Select a valid socket mode")
          .test(
            "datagram-available",
            "Datagram mode is not supported on Windows",
            (value) => value !== "datagram" || udsDatagramAvailable
          )
          .when("type", {
            is: (value: string) => value?.toLowerCase?.() === "uds",
            then: (schema) => schema.required("Socket mode is required"),
            otherwise: (schema) => schema.notRequired()
          }),
        url: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .test("url-required", "URL is required", function (value) {
            const type = (this.parent?.type ?? "").toLowerCase();
            const destinationMode = (this.parent?.destinationMode ?? "configured").toLowerCase();
            return type === "rtsp" || type === "llhls" || type === "rawpcap" || (type === "udp" && destinationMode === "captured") ? true : Boolean(value);
          })
          .test("valid-url", "Enter a valid URL", function (value) {
            const type = (this.parent?.type ?? "").toLowerCase();
            const destinationMode = (this.parent?.destinationMode ?? "configured").toLowerCase();
            if (type === "rtsp" || type === "llhls" || type === "rawpcap" || (type === "udp" && destinationMode === "captured")) {
              return true;
            }
            if (!value) {
              return true;
            }
            if (type === "whip") return !validateWhip(this.parent.whip, value);
            const expectedPrefix = getTypeProtocolPrefix(type as StreamTargetForm["type"]);
            const lowerValue = value.toLowerCase();
            if (type === "uds") {
              return isValidUdsSocketPathValue(value)
                ? true
                : this.createError({ message: "URL must start with UDS or be a socket path/name" });
            }
            if (!lowerValue.startsWith(expectedPrefix.toLowerCase())) {
              return this.createError({ message: `URL must start with ${expectedPrefix.toUpperCase()}` });
            }
            if (type === "udp") {
              const isValidUdpUrl = (() => {
                try {
                  const parsed = new URL(value);
                  if (parsed.protocol.toLowerCase() !== "udp:") {
                    return false;
                  }
                  if (!isValidIpLiteral(parsed.hostname)) {
                    return false;
                  }
                  const port = Number(parsed.port);
                  return Number.isInteger(port) && port >= 1 && port <= 65535;
                } catch {
                  return false;
                }
              })();
              return isValidUdpUrl
                ? true
                : this.createError({ message: "UDP URL must match udp://<ip>:<port>" });
            }
            try {
              // eslint-disable-next-line no-new
              new URL(value);
              return true;
            } catch {
              return this.createError({ message: "Enter a valid URL" });
            }
          }),
        networkInterface: Yup.string().transform((value) => value?.trim() ?? ""),
        srt: Yup.object({
          latency: Yup.number()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .nullable()
            .typeError("Latency must be a number")
            .test("min", "Latency must be at least 80 ms", (value) => value == null || value >= 80)
            .test("max", "Latency must be at most 8000 ms", (value) => value == null || value <= 8000),
          passphrase: Yup.string()
            .nullable()
            .transform((value) => value?.trim() ?? "")
            .test("passphrase-length", "Passphrase must be between 10 and 78 characters", (value) => {
              if (!value) {
                return true;
              }
              return value.length >= 10 && value.length < 79;
            }),
          pbkeylen: Yup.number()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .nullable()
            .typeError("Pbkeylen must be a number")
            .when("passphrase", {
              is: (passphrase: string | null | undefined) => Boolean(passphrase && passphrase.trim()),
              then: (schema) => schema.required("Pbkeylen is required when Passphrase is set"),
              otherwise: (schema) => schema.nullable()
            }),
          streamid: Yup.string()
            .transform((value) => value?.trim() ?? "")
            .max(512, "Stream ID must be at most 512 characters")
            .test("no-null-char", "Stream ID cannot contain null characters", (value) =>
              value ? !/\u0000/.test(value) : true
            ),
          transtype: Yup.string()
            .transform((value) => value?.trim().toLowerCase() ?? "")
            .oneOf(["live", "file"], "Transport type is required")
            .required("Transport type is required"),
          blocking: Yup.boolean().required("Blocking is required")
        }).when("type", {
          is: (value: string) => value?.toLowerCase?.() === "srt",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired()
        })
      }),
    [normalizedReservedNames, udsDatagramAvailable]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (readOnlyRef.current) return;
        const ttl =
          typeof values.ttl === "number" && Number.isFinite(values.ttl)
            ? Math.max(1, Math.round(values.ttl))
            : 64;
        const requestedBufferSize =
          typeof values.bufferSize === "number" && Number.isFinite(values.bufferSize)
            ? Math.max(1, Math.round(values.bufferSize))
            : DEFAULT_TARGET_BUFFER_SIZE;
        const bufferSize = values.type === "srt"
          ? Math.max(requestedBufferSize, MINIMUM_SRT_OUTPUT_BUFFER_SIZE)
          : requestedBufferSize;
        const srtLatency: number | "" =
          typeof values.srt.latency === "number" && Number.isFinite(values.srt.latency)
            ? Math.max(0, Math.round(values.srt.latency))
            : "";
        const srtPbkeylen: number | "" =
          typeof values.srt.pbkeylen === "number" && Number.isFinite(values.srt.pbkeylen)
            ? Math.max(0, Math.round(values.srt.pbkeylen))
            : "";
        const srtPassphrase = values.srt.passphrase.trim();
        const srtPayload = {
          latency: srtLatency,
          passphrase: srtPassphrase,
          pbkeylen: srtPassphrase ? srtPbkeylen : "",
          streamid: values.srt.streamid.trim(),
          transtype: values.srt.transtype.trim() || "live",
          blocking: Boolean(values.srt.blocking)
        };
        onSave({
          ...values,
          name: values.name.trim(),
          description: values.description.trim(),
          url: values.url.trim(),
          networkInterface: values.networkInterface.trim(),
          ttl,
          bufferSize,
          srt: srtPayload,
          rawPcap: {
            ...values.rawPcap,
            interfaceId: values.rawPcap.interfaceId.trim(),
            destinationIp: values.rawPcap.destinationIp.trim(),
            destinationMac: values.rawPcap.destinationMac.trim(),
            destinationPort: typeof values.rawPcap.destinationPort === "number" ? Math.round(values.rawPcap.destinationPort) : "",
            queueSize: typeof values.rawPcap.queueSize === "number" ? Math.round(values.rawPcap.queueSize) : 256,
            allowSpoofedSource: Boolean(values.rawPcap.allowSpoofedSource)
          }
        });
      }}
      enableReinitialize
      validateOnMount
    >
      {({ values, errors, touched, handleChange: changeField, handleBlur, setFieldValue: updateFieldValue, handleSubmit, isValid }) => {
        const setFieldValue: typeof updateFieldValue = (...args) => readOnlyRef.current
          ? Promise.resolve()
          : updateFieldValue(...args);
        const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (!readOnlyRef.current) changeField(event);
        };
        const urlProtocolPrefix = getTypeProtocolPrefix(values.type);
        const urlFieldValue = urlProtocolPrefix
          ? stripTypeProtocolFromUrl(values.url, urlProtocolPrefix)
          : values.url;
        const multicastDestinationUrl =
          values.destinationMode === "captured" && capturedDestination
            ? `udp://${capturedDestination.ip}:${capturedDestination.port}`
            : values.url;
        const showNetworkInterfaceField = values.type === "udp" && isMulticastUrl(multicastDestinationUrl);
        if (!readOnly && !showNetworkInterfaceField && values.networkInterface.trim()) {
          setFieldValue("networkInterface", "");
        }
        if (!readOnly && !isEditing && values.type === "uds") {
          const expectedAutoUrl = buildDefaultUdsUrl(platformName, streamName, values.name);
          if (!values.url.trim() || values.url === lastAutoUrlRef.current) {
            if (values.url !== expectedAutoUrl) {
              setFieldValue("url", expectedAutoUrl);
            }
            lastAutoUrlRef.current = expectedAutoUrl;
          }
        }

        return (
          <Dialog open={open} onClose={onClose} fullWidth maxWidth={values.type === "llhls" ? "md" : "sm"}>
            <DefaultPropsProvider value={{
              MuiTextField: { disabled: readOnly },
              MuiAutocomplete: { disabled: readOnly },
              MuiSwitch: { disabled: readOnly }
            }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2.5, fontWeight: 600, letterSpacing: "-0.02em" }}>
              <OutputRounded sx={{ color: "primary.main", fontSize: 28 }} />
              {readOnly ? "Output target configuration" : isEditing ? "Edit output target" : "Add output target"}
            </DialogTitle>
            <DialogContent dividers sx={{ py: 2.5 }}>
              <Stack spacing={2}>
                <TextField
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={(event) => {
                    const nextName = event.target.value;
                    setFieldValue("name", nextName);
                    if (!isEditing && values.type === "uds") {
                      const nextAutoUrl = buildDefaultUdsUrl(platformName, streamName, nextName);
                      if (!values.url.trim() || values.url === lastAutoUrlRef.current) {
                        setFieldValue("url", nextAutoUrl);
                        lastAutoUrlRef.current = nextAutoUrl;
                      }
                    }
                  }}
                  onBlur={handleBlur}
                  required
                  fullWidth
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name ? errors.name : undefined}
                />
                <TextField
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                />
                {showInputSelector ? (
                  <TextField
                    name="inputType"
                    label="Input"
                    value={values.inputType}
                    onChange={(event) => {
                      const inputType = event.target.value as "source" | "processor";
                      setFieldValue("inputType", inputType);
                      setFieldValue("inputId", inputType === "processor" ? flywheelProcessorId : "");
                    }}
                    select
                    fullWidth
                  >
                    <MenuItem value="source">Original source</MenuItem>
                    <MenuItem value="processor" disabled={!flywheelEnabled || !["udp", "srt", "uds", "llhls", "whip", "rtsp"].includes(values.type)}>Flywheel</MenuItem>
                  </TextField>
                ) : null}
                {values.type === "udp" && <>
                  <Stack direction="row" spacing={2}>
                    <TextField select fullWidth label="Payload" name="payloadMode" value={values.payloadMode} onChange={handleChange} sx={{ flex: 1, minWidth: 0 }}>
                      <MenuItem value="fullStream">Full stream</MenuItem>
                      <MenuItem value="h264Sei">H.264 SEI metadata</MenuItem>
                    </TextField>
                    {values.payloadMode === "h264Sei" && <TextField select fullWidth label="UUID filter" name="sei.filterMode" value={values.sei.filterMode} onChange={handleChange} sx={{ flex: 1, minWidth: 0 }}>
                      <MenuItem value="specific">Specific UUID</MenuItem>
                      <MenuItem value="any">Any UUID</MenuItem>
                    </TextField>}
                  </Stack>
                  {values.payloadMode === "h264Sei" && <Stack spacing={2}>
                    {values.sei.filterMode === "specific" && <TextField fullWidth required label="UUID" name="sei.uuidFilter"
                      value={values.sei.uuidFilter} onChange={handleChange} onBlur={handleBlur}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      error={Boolean(touched.sei?.uuidFilter && validateSEI(values.sei))}
                      helperText={touched.sei?.uuidFilter ? validateSEI(values.sei) : "Selects the telemetry producer's 16-byte UUID."} />}
                    <FormControlLabel label="Include UUID in output" control={<Switch name="sei.includeUuid" checked={values.sei.includeUuid} onChange={handleChange} />} />
                  </Stack>}
                </>}
                {values.type === "udp" && capturedDestination ? (
                  <TextField
                    name="destinationMode"
                    label="Destination"
                    value={values.destinationMode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    select
                    fullWidth
                  >
                    <MenuItem value="configured">Custom destination</MenuItem>
                    <MenuItem value="captured">
                      Captured ({capturedDestination.ip}:{capturedDestination.port})
                    </MenuItem>
                  </TextField>
                ) : null}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  <Stack sx={{ flexBasis: { sm: "30%", md: "25%" }, minWidth: 140 }} flexShrink={0}>
                    <TextField
                      name="type"
                      label="Type"
                      value={values.type}
                      onChange={(event) => {
                        const nextType = event.target.value as StreamTargetForm["type"];
                        const previousProtocol = getTypeProtocolPrefix(values.type);
                        const urlSuffix = stripTypeProtocolFromUrl(values.url, previousProtocol);
                        const nextProtocol = getTypeProtocolPrefix(nextType);
                        const sanitizedSuffix = urlSuffix.replace(/^\s+/, "");
                        const nextUrl = sanitizedSuffix ? `${nextProtocol}${sanitizedSuffix}` : "";
                        setFieldValue("type", nextType);
                        if (nextType !== "udp") {
                          setFieldValue("payloadMode", "fullStream");
                          setFieldValue("sei", defaultSEI());
                          setFieldValue("destinationMode", "configured");
                        }
                        if (!["udp", "srt", "uds", "llhls", "whip", "rtsp"].includes(nextType)) {
                          setFieldValue("inputType", "source");
                          setFieldValue("inputId", "");
                        }
                        if (nextType === "uds") {
                          const nextAutoUrl = buildDefaultUdsUrl(platformName, streamName, values.name);
                          const shouldAuto = !sanitizedSuffix || values.url === lastAutoUrlRef.current;
                          setFieldValue("url", shouldAuto ? nextAutoUrl : nextUrl);
                          if (shouldAuto) {
                            lastAutoUrlRef.current = nextAutoUrl;
                          }
                          return;
                        }
                        setFieldValue("url", nextUrl);
                      }}
                      onBlur={handleBlur}
                      select
                      fullWidth
                      error={touched.type && Boolean(errors.type)}
                      helperText={
                        touched.type
                          ? errors.type
                          : values.type === "rawPcap" && !rawPcapEnabled
                            ? "Raw PCAP requires a PCAP source in Recast or Play and Recast mode, a Recast license, and a gopacket-enabled build."
                            : " "
                      }
                    >
                      {TARGET_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value} disabled={option.disabled || (option.value === "rawPcap" && !rawPcapEnabled)}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  {values.type === "rtsp" || values.type === "llhls" || values.type === "rawPcap" ? null : values.type !== "udp" || values.destinationMode !== "captured" ? (
                  <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                    <TextField
                      name="url"
                      label={values.type === "whip" ? "WHIP publishing endpoint" : "URL"}
                      value={urlFieldValue}
                      onChange={(event) => {
                        const sanitizedValue = event.target.value.replace(/^\s+/, "");
                        const lowerValue = sanitizedValue.toLowerCase();
                        const lowerPrefix = urlProtocolPrefix.toLowerCase();
                        const withoutPrefix = lowerValue.startsWith(lowerPrefix)
                          ? sanitizedValue.slice(urlProtocolPrefix.length)
                          : sanitizedValue;
                        const sanitizedSuffix = withoutPrefix.replace(/^\s+/, "");
                        const nextUrl = sanitizedSuffix ? `${urlProtocolPrefix}${sanitizedSuffix}` : "";
                        setFieldValue("url", nextUrl);
                      }}
                      onBlur={handleBlur}
                      required
                      fullWidth
                      error={touched.url && Boolean(errors.url)}
                      helperText={touched.url && errors.url ? errors.url : values.type === "whip" ? "HTTP(S) publishing URL supplied by your receiver; this is not a viewer URL" : " "}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              disableTypography
                              sx={{ color: "inherit", fontSize: "inherit", mr: 0 }}
                            >
                              {urlProtocolPrefix}
                            </InputAdornment>
                          )
                        }
                      }}
                    />
                  </Stack>
                  ) : (
                    <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                      <TextField
                        label="Resolved UDP destination"
                        value={`${capturedDestination?.ip ?? ""}:${capturedDestination?.port ?? ""}`}
                        fullWidth
                        disabled
                        helperText="Updated automatically when the selected capture flow changes"
                      />
                    </Stack>
                  )}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ height: 56, flexShrink: 0, minWidth: { sm: 40 }, width: { xs: "100%", sm: "auto" } }}
                  >
                    <Tooltip title="Advanced" placement="top">
                      <IconButton
                        color={showAdvanced ? "primary" : "default"}
                        size="small"
                        onClick={() => setShowAdvanced((prev) => !prev)}
                        aria-pressed={showAdvanced}
                        aria-label="Advanced"
                        sx={(theme) => ({
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, showAdvanced ? 0.16 : 0.08),
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                        })}
                      >
                        <TuneRounded />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {values.type === "rtsp" && <Stack spacing={1.5}><RTSPTargetFields value={values.rtsp} onChange={v => { if (!readOnlyRef.current) setFieldValue("rtsp", v); }} />{errors.rtsp && <Alert severity="error">{String(errors.rtsp)}</Alert>}</Stack>}
                {values.type === "whip" && <Stack spacing={1.5}>
                  <WhipParams value={values.whip} onChange={v => { if (!readOnlyRef.current) setFieldValue("whip",v); }} />
                  {errors.whip && <Alert severity="error">{String(errors.whip)}</Alert>}
                </Stack>}
                {values.type === "llhls" ? <Stack spacing={1.5}>
                  <TextField label="Playback URL" value={values.llhls.hlsPlaybackUrl} onChange={event => setFieldValue("llhls.hlsPlaybackUrl", event.target.value)} helperText="Optional stable HTTP(S) URL ending in /index.m3u8. This address is hosted by SkyView." />
                  <LlHlsParams value={values.llhls} onChange={(value: Record<string, string | number>) => { if (!readOnlyRef.current) setFieldValue("llhls", value); }} disabled={readOnly} />
                  {errors.llhls && <Alert severity="error">{String(errors.llhls)}</Alert>}
                </Stack> : null}
                {values.type === "uds" ? (
                  <TextField
                    name="udsMode"
                    label="Socket mode"
                    value={values.udsMode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    select
                    fullWidth
                    required
                    error={touched.udsMode && Boolean(errors.udsMode)}
                    helperText={touched.udsMode ? errors.udsMode : udsDatagramAvailable ? "Connection uses a sender-owned stream server; Datagram uses a receiver-owned packet socket." : "Datagram mode is not supported on Windows."}
                  >
                    <MenuItem value="connection">Connection (stream)</MenuItem>
                    <MenuItem value="datagram" disabled={!udsDatagramAvailable}>Datagram</MenuItem>
                  </TextField>
                ) : null}
				{values.type === "rawPcap" ? (
				  <RawPcapFields
                    readOnly={readOnly}
				    enabled={rawPcapEnabled}
				    source={pcapSource}
				    authorizedFetch={authorizedFetch}
				    setPreflightReady={setRawPreflightReady}
				  />
				) : null}
                {showAdvanced ? (
                  values.type === "srt" ? (
                    <SrtInputParams name="srt" mode={readOnly ? "view" : "edit"} />
                  ) : values.type === "udp" || values.type === "uds" ? (
                    <Stack
                      spacing={2}
                      direction={{ xs: "column", sm: "row" }}
                      alignItems="flex-start"
                      sx={{ width: "100%" }}
                    >
                      {values.type === "udp" ? (
                        <TextField
                          name="ttl"
                          label="TTL"
                          type="number"
                          value={values.ttl}
                          onChange={(event) => {
                            const next = event.target.value;
                            const parsed = next === "" ? "" : Number(next);
                            setFieldValue("ttl", Number.isFinite(parsed as number) ? parsed : "");
                          }}
                          onBlur={handleBlur}
                          fullWidth
                          error={touched.ttl && Boolean(errors.ttl)}
                          helperText={touched.ttl ? errors.ttl : " "}
                          slotProps={{ htmlInput: { min: 1, max: 255 } }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                      ) : null}
                      {values.type === "udp" || values.type === "uds" ? (
                        <TextField
                          name="bufferSize"
                          label="Buffer size"
                          type="number"
                          value={values.bufferSize}
                          onChange={(event) => {
                            const next = event.target.value;
                            const parsed = next === "" ? "" : Number(next);
                            setFieldValue("bufferSize", Number.isFinite(parsed as number) ? parsed : "");
                          }}
                          onBlur={handleBlur}
                          fullWidth
                          error={touched.bufferSize && Boolean(errors.bufferSize)}
                          helperText={touched.bufferSize ? errors.bufferSize : " "}
                          slotProps={{ htmlInput: { min: 1 } }}
                          sx={{
                            flex: values.type === "uds" ? 0.2 : 1,
                            minWidth: 0
                          }}
                        />
                      ) : null}
                    </Stack>
                  ) : null
                ) : null}
                {showNetworkInterfaceField ? (
                  <Autocomplete
                    freeSolo
                    options={networkInterfaceOptions}
                    value={values.networkInterface}
                    onOpen={() => {
                      void loadNetworkInterfaces(values.networkInterface);
                    }}
                    onChange={(_, newValue) => {
                      setFieldValue("networkInterface", (newValue ?? "").trim());
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input") {
                        setFieldValue("networkInterface", newInputValue);
                      }
                    }}
                    loading={isNetworkInterfacesLoading}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="networkInterface"
                        label="Network interface"
                        fullWidth
                        onBlur={handleBlur}
                        error={touched.networkInterface && Boolean(errors.networkInterface)}
                        helperText={touched.networkInterface ? errors.networkInterface : " "}
                      />
                    )}
                  />
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button variant="outlined" onClick={onClose}>
                {readOnly ? "Close" : "Cancel"}
              </Button>
              {!readOnly ? <Button variant="contained" onClick={() => handleSubmit()} disabled={!isValid || (values.type === "rawPcap" && !rawPreflightReady)}>
                {isEditing ? "Save" : "Add"}
              </Button> : null}
            </DialogActions>
            </DefaultPropsProvider>
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default StreamTargetDlg;
